/**
 * Rabattkoder – format, generering och validering.
 *
 * Formatet är `LB-XXXX-XXXX`: 8 tecken ur ett alfabet där visuellt
 * förväxlingsbara tecken är borttagna (inga I, L, O, U). Det åttonde tecknet
 * är en kontrollsiffra, så ett feltryck fångas direkt i webbläsaren i stället
 * för att ge "koden finns inte" efter en databasslagning.
 *
 * Alfabetet är Crockford Base32, vilket också ger felkorrigering vid inmatning:
 * skriver köparen O tolkas det som 0, skriver hen I eller L tolkas det som 1.
 */

/** 32 tecken. Ingen I, L, O eller U. */
export const CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'

/** Prefix på den tryckta lappen. Ingår inte i det som lagras i databasen. */
export const CODE_PREFIX = 'LB'

/** Antal tecken totalt, inklusive kontrolltecknet. */
export const CODE_LENGTH = 8

const DECODE_MAP: Record<string, string> = (() => {
  const map: Record<string, string> = {}
  for (const ch of CODE_ALPHABET) map[ch] = ch
  // Crockfords felkorrigering
  map['O'] = '0'
  map['I'] = '1'
  map['L'] = '1'
  // U utelämnas medvetet utan mappning – den finns inte i alfabetet och ska
  // ge ett tydligt fel i stället för att gissas till V.
  return map
})()

/**
 * Rensar användarinmatning till kanonisk form: versaler, inga skiljetecken,
 * inget LB-prefix, förväxlingsbara tecken översatta.
 *
 * Returnerar null om något tecken inte går att tolka eller längden är fel.
 */
export function normalizeCode(input: string): string | null {
  if (!input) return null

  let raw = input.toUpperCase().replace(/[^0-9A-Z]/g, '')
  if (raw.startsWith(CODE_PREFIX)) raw = raw.slice(CODE_PREFIX.length)
  if (raw.length !== CODE_LENGTH) return null

  let out = ''
  for (const ch of raw) {
    const mapped = DECODE_MAP[ch]
    if (!mapped) return null
    out += mapped
  }
  return out
}

/** Beräknar kontrolltecknet för de första CODE_LENGTH-1 tecknen. */
function checkChar(body: string): string {
  let sum = 0
  for (let i = 0; i < body.length; i++) {
    const value = CODE_ALPHABET.indexOf(body[i])
    if (value < 0) throw new Error(`Ogiltigt tecken i kod: ${body[i]}`)
    // Viktad summa – fångar både enstaka feltryck och omkastade tecken.
    sum += value * (i + 2)
  }
  return CODE_ALPHABET[sum % CODE_ALPHABET.length]
}

/** True om koden är korrekt formaterad OCH kontrolltecknet stämmer. */
export function isValidCode(input: string): boolean {
  const normalized = normalizeCode(input)
  if (!normalized) return false
  const body = normalized.slice(0, CODE_LENGTH - 1)
  return checkChar(body) === normalized[CODE_LENGTH - 1]
}

/**
 * Gör om en lagrad kod till läsbar form för tryck och skärm: `LB-A3K9-M2XP`.
 */
export function formatCode(normalized: string): string {
  const a = normalized.slice(0, 4)
  const b = normalized.slice(4, 8)
  return `${CODE_PREFIX}-${a}-${b}`
}

/**
 * Genererar en ny kod. `randomBytes` injiceras så att funktionen kan användas
 * både i Cloud Functions (node:crypto) och i webbläsaren (crypto.getRandomValues)
 * utan att den här filen behöver känna till någon av dem.
 */
export function generateCode(randomBytes: (n: number) => Uint8Array): string {
  const bodyLength = CODE_LENGTH - 1
  let body = ''
  // Rejection sampling: 256 är inte jämnt delbart med 32 – jo det är det
  // (8 x 32 = 256), så varje byte ger ett perfekt likformigt tecken.
  const bytes = randomBytes(bodyLength)
  for (let i = 0; i < bodyLength; i++) {
    body += CODE_ALPHABET[bytes[i] % CODE_ALPHABET.length]
  }
  return body + checkChar(body)
}

/** Länken som QR-koden på den fysiska lappen pekar på. */
export function activationUrl(baseUrl: string, normalizedCode: string): string {
  const trimmed = baseUrl.replace(/\/+$/, '')
  return `${trimmed}/aktivera?kod=${formatCode(normalizedCode)}`
}
